import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { JobService } from './services/job.service';
import {
  Input,
  Select,
  Upload,
  Button,
  Spin,
  message,
  UploadProps,
} from 'antd';
import {
  BellOutlined,
  InboxOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Subscription } from '../../../core/models/subscription/subscription.model';
import { SubscriptionService } from './services/subscription.service';
import { CategoryService } from './services/category.service';
import Swal from 'sweetalert2';
import PublicActionPage from '../components/PublicActionPage';
import PublicConsentCheckbox from '../components/PublicConsentCheckbox';
import PublicFormField from '../components/PublicFormField';

const { Dragger } = Upload;

const Subscribe: React.FC = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileSpinner, setFileSpinner] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [subscription, setSubscription] = useState<Subscription>(
    new Subscription()
  );

  const [inputErrors, setInputErrors] = useState<{
    [key: string]: string | null;
  }>({});

  const [subCategoriesSubscription, setSubCategoriesSubscription] = useState<
    string[]
  >([]);
  const [categoriesOptions, setCategoriesOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  function isValidEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  const validateSubscription = () => {
    const newErrors: { [key: string]: string | null } = {};

    if (!subscription.firstName.trim())
      newErrors.firstName = 'First name is required';
    if (!subscription.lastName.trim())
      newErrors.lastName = 'Last name is required';

    if (!subscription.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(subscription.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (subscription.subCategories.length === 0)
      newErrors.subCategories = 'Select at least one technology area';

    setInputErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateSubscription = async () => {
    try {
      setSubmitting(true);
      const response = await SubscriptionService.AddSubscription(subscription);
      if (response) {
        setIsSubscribed(true);
        setHasConsented(false);
      }
    } catch (error: any) {
      const newErrors: { [key: string]: string | null } = {};
      newErrors.email = 'This email is already subscribed';
      setInputErrors(newErrors);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: error.message,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const ExtractRegistrationInfo = async (file: File) => {
    try {
      setFileSpinner(true);
      const response = await JobService.RegistrationInfoExtraction(file);
      setFileSpinner(false);

      if (response) {
        setSubscription((prevSubscription) => ({
          ...prevSubscription,
          firstName: response.first_name || '',
          lastName: response.last_name || '',
          email: response.email || '',
        }));
        setInputErrors({});
      }
    } catch (error: any) {
      setFileSpinner(false);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: error.message,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });
    }
  };

  const handleFileChange = (info: any) => {
    if (info.file && info.file.status && info.file.status === 'removed') {
      setFile(null);
    } else if (info.file) {
      setFile(info.file);
      ExtractRegistrationInfo(info.file);
      setInputErrors((prev) => ({
        ...prev,
        file: null,
      }));
    }
  };

  const uploadProps: UploadProps = {
    multiple: false,
    maxCount: 1,
    accept: '.pdf,.docx',
    beforeUpload(file) {
      setFileSpinner(true);
      const isPDF = file.type === 'application/pdf';
      const isDOCX =
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      if (!isPDF && !isDOCX) {
        message.error('Only PDF or DOCX files are allowed.');
        setFileSpinner(false);
        return Upload.LIST_IGNORE;
      }

      return false;
    },
    onChange: handleFileChange,
    onRemove() {
      setFile(null);
    },
    disabled: isSubscribed,
  };

  const onChangeSubscriptionCategories = (values: string[]) => {
    setSubCategoriesSubscription(values);

    setSubscription((prevSubscription) => ({
      ...prevSubscription,
      subCategories: values,
    }));
    setInputErrors((prev) => ({
      ...prev,
      subCategories: null,
    }));
  };

  const OnChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setSubscription((prevSubscription) => ({
      ...prevSubscription,
      [name]: value,
    }));
    setInputErrors((prev) => ({
      ...prev,
      [name]: null,
    }));
  };

  useEffect(() => {
    let cancelled = false;

    async function fetchSubCategories() {
      try {
        const resp = await CategoryService.GetSubCategories();
        if (!cancelled) {
          setCategoriesOptions(resp.map((c) => ({ value: c, label: c })));
          setCategoriesError(null);
        }
      } catch {
        if (!cancelled) {
          setCategoriesError(
            'Technology areas could not be loaded. You can still type your own areas.'
          );
        }
      }
    }

    fetchSubCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PublicActionPage
      eyebrow="Job Alerts"
      title="Jobs that match you, in your inbox"
      description="Tell us what you work with. We’ll email matching roles daily — no account required."
      icon={<BellOutlined />}
      steps={[
        {
          title: 'Share your details',
          detail: 'Upload a resume to prefill, or type them in.',
        },
        {
          title: 'Pick technology areas',
          detail: 'Java, React, DevOps — whatever you want alerts for.',
        },
        {
          title: 'Confirm by email',
          detail: 'Activate once, then get a daily digest of matches.',
        },
      ]}
      asideNote="We only use your resume to prefill this form. You can edit every field before subscribing."
      formEyebrow="Alert setup"
      formTitle="Create your job alert"
      formDescription="Resume upload is optional. Choose at least one technology area."
      footer={
        <p className="jo-body-muted mb-0 text-center text-sm">
          Prefer browsing now?{' '}
          <Link to="/jobs" className="public-inline-link">
            Find open jobs
          </Link>
        </p>
      }
    >
      <div className="mx-auto w-full max-w-xl">
        {isSubscribed && (
          <div
            className="public-result-panel public-result-panel--success mb-5"
            role="status"
            aria-live="polite"
          >
            <p className="jo-success-banner mb-3 text-sm font-semibold">
              <CheckSquareOutlined className="mr-2" aria-hidden="true" />
              Alert saved. Check your inbox to confirm activation.
            </p>
            <Link to="/jobs" className="public-button-primary inline-flex">
              <SearchOutlined aria-hidden="true" />
              Browse open jobs
            </Link>
          </div>
        )}

        {!isSubscribed && (
          <>
            <div className="public-form-field mb-5">
              <span className="public-form-label" id="resume-upload-label">
                Resume (optional)
              </span>
              <p className="public-form-hint mb-2">
                PDF or DOCX — we use it only to prefill your name and email.
              </p>
              <Dragger
                {...uploadProps}
                showUploadList={false}
                className="public-upload-zone flex min-h-[180px] flex-col justify-center rounded-lg"
                aria-labelledby="resume-upload-label"
              >
                {fileSpinner ? (
                  <div className="flex flex-col items-center gap-2 px-3">
                    <Spin />
                    <p className="jo-body-muted mb-0 text-sm">
                      Reading your resume…
                    </p>
                  </div>
                ) : file ? (
                  <>
                    <p
                      className="ant-upload-drag-icon"
                      style={{ marginBottom: 0 }}
                    >
                      <FileTextOutlined style={{ fontSize: '36px' }} />
                    </p>
                    <p className="mb-1">
                      <strong>{file.name}</strong>
                    </p>
                    <p className="jo-body-muted mb-2 text-sm">
                      Details prefilled below — edit anything that looks wrong.
                    </p>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      Remove file
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined style={{ fontSize: '36px' }} />
                    </p>
                    <p className="ant-upload-text">Drag & drop or browse</p>
                    <p className="ant-upload-hint">PDF or DOCX</p>
                  </>
                )}
              </Dragger>
            </div>

            <div className="grid w-full grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4">
              <PublicFormField
                id="alert-first-name"
                label="First name"
                error={inputErrors.firstName}
              >
                <Input
                  name="firstName"
                  placeholder="Ada"
                  value={subscription.firstName}
                  onChange={OnChangeInput}
                  status={inputErrors.firstName ? 'error' : ''}
                  autoComplete="given-name"
                />
              </PublicFormField>

              <PublicFormField
                id="alert-last-name"
                label="Last name"
                error={inputErrors.lastName}
              >
                <Input
                  name="lastName"
                  placeholder="Lovelace"
                  value={subscription.lastName}
                  onChange={OnChangeInput}
                  status={inputErrors.lastName ? 'error' : ''}
                  autoComplete="family-name"
                />
              </PublicFormField>

              <PublicFormField
                id="alert-email"
                label="Email"
                error={inputErrors.email}
                className="sm:col-span-2"
              >
                <Input
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={subscription.email}
                  onChange={OnChangeInput}
                  status={inputErrors.email ? 'error' : ''}
                  autoComplete="email"
                />
              </PublicFormField>

              <PublicFormField
                id="alert-tech-areas"
                label="Technology areas"
                error={inputErrors.subCategories}
                hint={
                  categoriesError ??
                  'Select from the list or type your own areas.'
                }
                className="sm:col-span-2"
              >
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="e.g. Java, React, DevOps"
                  value={subCategoriesSubscription}
                  onChange={onChangeSubscriptionCategories}
                  options={categoriesOptions}
                  maxTagCount={4}
                  status={inputErrors.subCategories ? 'error' : ''}
                  aria-label="Technology areas"
                />
              </PublicFormField>

              <div className="sm:col-span-2">
                <PublicConsentCheckbox
                  id="job-alert-consent"
                  checked={hasConsented}
                  disabled={submitting}
                  purpose="receive daily job alerts and to the use of my information"
                  onChange={setHasConsented}
                  className="mb-3"
                />
                <Button
                  type="primary"
                  className="public-button-primary mt-1 w-full"
                  loading={submitting}
                  disabled={fileSpinner || submitting || !hasConsented}
                  onClick={async () => {
                    if (hasConsented && validateSubscription()) {
                      await generateSubscription();
                    }
                  }}
                >
                  Subscribe to daily jobs
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </PublicActionPage>
  );
};

export default Subscribe;
